import { FC, ReactNode, useMemo } from "react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTranslation } from "../../../hooks/useTranslation";

/**
 * Emotion caches for each writing direction. The RTL cache runs the stylis RTL
 * plugin so MUI's emotion-generated styles (padding/margin/positioning) mirror
 * for Hebrew; the LTR cache is the plain prefixer. `prepend` keeps MUI's base
 * styles first in <head> so app overrides still win.
 */
const ltrCache = createCache({ key: "muiltr", stylisPlugins: [prefixer], prepend: true });
const rtlCache = createCache({ key: "muirtl", stylisPlugins: [prefixer, rtlPlugin], prepend: true });

interface Props {
  children: ReactNode;
}

/**
 * Switches the emotion cache and the MUI theme direction based on the active
 * language, so the whole component tree mirrors for Hebrew (RTL) and flips back
 * for LTR languages. Pairs with the <html dir> toggle in App (which drives plain
 * text alignment); this layer handles MUI component mirroring.
 */
export const DirectionProvider: FC<Props> = ({ children }) => {
  const { i18n } = useTranslation();
  const isRtl = (i18n.language || "").toLowerCase().startsWith("he");

  const theme = useMemo(
    () => createTheme({ direction: isRtl ? "rtl" : "ltr" }),
    [isRtl]
  );

  return (
    <CacheProvider value={isRtl ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CacheProvider>
  );
};

export default DirectionProvider;
